import { useCallback, useEffect, useState } from 'react';

export const CONSENT_KEY = 'karaokr_consent_v2';
export const CONSENT_VERSION = 2;

export interface ConsentState {
  acceptedAt: string;
  terms: boolean;
  version: number;
}

function loadConsent(): ConsentState | null {
  try {
    const raw = localStorage.getItem(CONSENT_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as ConsentState;
    if (parsed.version !== CONSENT_VERSION) return null;
    return parsed;
  } catch {
    return null;
  }
}

function saveConsent(state: ConsentState) {
  localStorage.setItem(CONSENT_KEY, JSON.stringify(state));
}

export function hasConsentedTerms(): boolean {
  const consent = loadConsent();
  return consent?.terms === true;
}

export function grantTermsConsent() {
  saveConsent({
    acceptedAt: new Date().toISOString(),
    terms: true,
    version: CONSENT_VERSION,
  });
}

export function revokeTermsConsent() {
  localStorage.removeItem(CONSENT_KEY);
}

export function useConsent() {
  const [consented, setConsented] = useState<boolean>(() => hasConsentedTerms());

  const refresh = useCallback(() => {
    setConsented(hasConsentedTerms());
  }, []);

  const grant = useCallback(() => {
    grantTermsConsent();
    refresh();
  }, [refresh]);

  const revoke = useCallback(() => {
    revokeTermsConsent();
    refresh();
  }, [refresh]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  return { consented, grant, revoke, refresh };
}
